import React, { useCallback, useRef, useState } from 'react';

import APIHandler from 'config/APIHandler/APIHandler';
import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';

import { useUserInfo } from 'hooks/useUserInfo';

import 'react-image-crop/dist/ReactCrop.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import { Redirect } from 'react-router-dom';
import SweetAlert from 'sweetalert-react';

export const ProfileImageView = () => {
  const { user, refresh } = useUserInfo();

  const [crop, setCrop] = useState();
  const [src, setSrc] = useState(null);
  const [redirect, setRedirect] = useState(null);
  const [loadingNewFile, setLoadingNewFile] = useState(false);
  const [showToLargePopup, setToLargePopup] = useState(false);
  const [hideCurrentImage, setHideCurrentImage] = useState(false);
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);

  const profileImage = hideCurrentImage ? null : (user?.profile_image ?? null);

  const imageRef = useRef(null);

  const closeToLargePopup = () => {
    setToLargePopup(false);
  };

  const resetUploadedFile = useCallback(() => {
    setSrc(null);
    setHideCurrentImage(true);
  }, []);

  const cancelUpload = useCallback(() => {
    setSrc(null);
    setHideCurrentImage(false);
  }, []);

  const uploadFile = useCallback(async () => {
    setLoadingNewFile(true);

    const accessToken = await StorageHandler.load(storageKeys.accessToken);
    const completed = await APIHandler.setProfileImage(
      accessToken,
      croppedImageUrl,
    );

    if (completed) {
      refresh();
      setLoadingNewFile(false);
      setRedirect('/main');
    }
  }, [croppedImageUrl, refresh]);

  const onSelectFile = useCallback((e) => {
    const fileSize = (e.target.files?.[0]?.size ?? 0) / 1024 / 1024;
    if (fileSize > 10) {
      setToLargePopup(true);
      e.target.value = '';
      return;
    }
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined);
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setSrc(reader.result);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  }, []);

  const onCropChange = useCallback((_, percentCrop) => {
    setCrop(percentCrop);
  }, []);

  const onImageLoaded = useCallback((e) => {
    imageRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const isLandscape = width > height;
    setCrop(
      centerCrop(
        makeAspectCrop(
          isLandscape ? { unit: '%', height: 100 } : { unit: '%', width: 100 },
          1,
          width,
          height,
        ),
        width,
        height,
      ),
    );
  }, []);

  const getCroppedImage = (image, crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) return Promise.reject(new Error('Could not get canvas context'));

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height,
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }, 'image/jpeg');
    });
  };

  const makeClientCrop = useCallback(async (crop) => {
    if (imageRef.current !== null && crop.width && crop.height) {
      const croppedImage = await getCroppedImage(imageRef.current, crop);
      setCroppedImageUrl(croppedImage);
    }
  }, []);

  const onCropComplete = useCallback(
    (pixelCrop) => {
      void makeClientCrop(pixelCrop);
    },
    [makeClientCrop],
  );

  return (
    <div className="profile-image-view">
      <div className="profile-image-view__holder">
        {profileImage && (
          <div className="profile-image-view__title">
            <span>{i18n.translate('profileImageView.currentImage')}</span>
            <img
              src={`https://dbjc44r0kfwxy.cloudfront.net/${profileImage}`}
              alt="profile-img"
              className="profile-image-view__current-image"
            />
            <div className="profile-image-view__button-holder profile-image-view__button-holder--centered">
              <button
                className={clsx(
                  'profile-image-view__button',
                  'profile-image-view__button--upload',
                )}
                type="button"
                onClick={resetUploadedFile}
              >
                {i18n.translate('profileImageView.uploadButton')}
              </button>
            </div>
          </div>
        )}
        {!profileImage && (
          <React.Fragment>
            <div className="profile-image-view__title">
              <span>{i18n.translate('profileImageView.uploadImage')}</span>
            </div>
            {!src && (
              <div className="profile-image-view__uploader">
                <input
                  type="file"
                  accept="image/*"
                  onChange={onSelectFile}
                  className="profile-image-view__file-input profile-image-view__file-input--4"
                  name="file"
                  id="file"
                />
                <label
                  htmlFor="file"
                  className="profile-image-view__file-label profile-image-view__file-label--4"
                >
                  <figure>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="17"
                      viewBox="0 0 20 17"
                    >
                      <path d="M10 0l-5.2 4.9h3.3v5.1h3.8v-5.1h3.3l-5.2-4.9zm9.3 11.5l-3.2-2.1h-2l3.4 2.6h-3.5c-.1 0-.2.1-.2.1l-.8 2.3h-6l-.8-2.2c-.1-.1-.1-.2-.2-.2h-3.6l3.4-2.6h-2l-3.2 2.1c-.4.3-.7 1-.6 1.5l.6 3.1c.1.5.7.9 1.2.9h16.3c.6 0 1.1-.4 1.3-.9l.6-3.1c.1-.5-.2-1.2-.7-1.5z" />
                    </svg>
                  </figure>{' '}
                  <span>{i18n.translate('profileImageView.chooseAFile')}</span>
                </label>
                {user?.profile_image && (
                  <button
                    className="profile-image-view__skip"
                    type="button"
                    onClick={cancelUpload}
                  >
                    {i18n.translate('profileImageView.cancel')}
                  </button>
                )}
              </div>
            )}
            {src && (
              <React.Fragment>
                <div className="profile-image-view__crop-holder">
                  <ReactCrop
                    crop={crop}
                    onChange={onCropChange}
                    onComplete={onCropComplete}
                    aspect={1}
                    circularCrop
                  >
                    <img
                      ref={imageRef}
                      alt="Crop me"
                      src={src}
                      onLoad={onImageLoaded}
                    />
                  </ReactCrop>
                </div>
                <div className="profile-image-view__button-holder">
                  <button
                    className={clsx(
                      'profile-image-view__button',
                      'profile-image-view__button--upload',
                    )}
                    onClick={resetUploadedFile}
                  >
                    {i18n.translate('profileImageView.uploadButton')}
                  </button>
                  <button
                    onClick={() => void uploadFile()}
                    disabled={!croppedImageUrl}
                  >
                    {loadingNewFile ? (
                      <span>
                        <FontAwesomeIcon icon={['fas', 'circle-notch']} spin />
                      </span>
                    ) : (
                      <span>
                        <FontAwesomeIcon icon={['far', 'upload']} />
                        {i18n.translate('profileImageView.saveButton')}
                      </span>
                    )}
                  </button>
                </div>
              </React.Fragment>
            )}
          </React.Fragment>
        )}

        <SweetAlert
          title={i18n.translate('profileImageView.fileTooLargeTitle')}
          show={showToLargePopup}
          html
          type="warning"
          text={i18n.translate('profileImageView.fileTooLargeText')}
          customClass="noAgent"
          showConfirmButton={false}
          cancelButtonText={i18n.translate('general.close')}
          showCancelButton
          onConfirm={closeToLargePopup}
          onCancel={closeToLargePopup}
          onEscapeKey={closeToLargePopup}
        />
      </div>
      {redirect && <Redirect push to={redirect} />}
    </div>
  );
};
